import { useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { MRT_Row, MRT_TableInstance } from 'mantine-react-table';
import { Alert, Box, Fieldset, Flex, Paper } from '@mantine/core';
import { ruleSchema, RuleMode } from '@billing/validation/rules';
import type { Rule } from '@billing/validation/rules';
import { useRuleBuilder } from './shared/useRuleBuilder';
import { getRuleCriteriaDescription } from './shared/ruleUtils';
import { getRuleAction, RuleActionPicker } from './shared/RuleActionPicker';
import { RuleCriteriaFields } from './shared/RuleCriteriaFields';
import { RuleOutcomeFields } from './shared/RuleOutcomeFields';
import {
  RuleCreateSteps,
  RuleCreateFooter,
} from './create/RuleCreateNavigation';
import { RuleCreateReview } from './create/RuleCreateReview';
import { RuleCreatePreview } from './shared/RuleSummary';

interface RuleCreateModalProps {
  row: MRT_Row<Rule>;
  table: MRT_TableInstance<Rule>;
  siteID: string;
  userID: string;
  timezone: string;
  canCreate: boolean;
}
export const RuleCreateModal = ({
  row,
  table,
  siteID,
  userID,
  canCreate,
  timezone,
}: RuleCreateModalProps): ReactElement => {
  const builder = useRuleBuilder({ timezone });
  const [step, setStep] = useState(0);
  const [publish, setPublish] = useState(true);
  const [furthestStep, setFurthestStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [isNameManuallyEdited, setIsNameManuallyEdited] = useState(false);
  const submitLock = useRef(false);
  const headingRef = useRef<HTMLDivElement>(null);

  const closeModal = (): void => table.setCreatingRow(null);
  const stepValid = (index: number): boolean => {
    if (index === 0) return true;
    if (index === 1) return builder.criteriaValid;
    if (index === 3) {
      return (
        builder.canSubmit &&
        ruleSchema.shape.name.safeParse(builder.ruleName).success
      );
    }
    return builder.outcomeValid;
  };

  const canVisit = (target: number): boolean =>
    target <= furthestStep &&
    Array.from({ length: target }, (_, index) => index).every(stepValid);

  const goTo = (target: number): void => {
    if (target === 3 && !isNameManuallyEdited) {
      const action = getRuleAction(builder.selectedAction, builder.translate);
      const name = action
        ? `${action.label}: ${getRuleCriteriaDescription(builder)}`
        : builder.effectiveRuleName;
      builder.setRuleName(name.slice(0, 255));
    }
    setStep(target);
    setFurthestStep((current) => Math.max(current, target));
    headingRef.current?.focus();
  };

  const submit = async (): Promise<void> => {
    if (!stepValid(3) || !canCreate || submitLock.current) return;
    submitLock.current = true;
    setSaving(true);
    setFailed(false);
    try {
      const values: Record<string, unknown> = {
        name: builder.effectiveRuleName,
        body: builder.formBody,
        siteID,
        createdBy: userID,
        ruleSetID: crypto.randomUUID(),
        active: publish,
        status: publish ? 'published' : 'draft',
      };
      await table.options.onCreatingRowSave({
        row,
        table,
        values,
        exitCreatingMode: closeModal,
      });
      closeModal();
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
      submitLock.current = false;
    }
  };
  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      gap="lg"
      align="flex-start"
      data-testid="create-rule-modal"
    >
      <Paper
        miw={0}
        w="100%"
        style={{ flex: 1, overflow: 'hidden' }}
        withBorder
        radius="lg"
        shadow="xs"
      >
        <RuleCreateSteps
          step={step}
          furthestStep={furthestStep}
          disabled={saving}
          canVisit={canVisit}
          onChange={goTo}
          translate={builder.translate}
        />
        <Box ref={headingRef} tabIndex={-1}>
          <Fieldset disabled={saving} variant="unstyled" m={'xl'} miw={0}>
            {step === 0 && (
              <RuleActionPicker
                value={builder.selectedAction}
                onChange={builder.onActionChange}
                translate={builder.translate}
              />
            )}
            {step === 1 && <RuleCriteriaFields builder={builder} />}
            {step === 2 && <RuleOutcomeFields builder={builder} />}
            {step === 3 && (
              <RuleCreateReview
                builder={builder}
                onNameChange={(name) => {
                  setIsNameManuallyEdited(true);
                  builder.setRuleName(name);
                }}
                publish={publish}
                onPublishChange={setPublish}
              />
            )}
          </Fieldset>
          {failed && (
            <Alert color="red" role="alert" mt="md">
              {builder.translate('pages:rules.builder.createFailed')}
            </Alert>
          )}
        </Box>
        <RuleCreateFooter
          step={step}
          saving={saving}
          valid={stepValid(step)}
          canCreate={canCreate}
          publish={publish}
          invalidMessage={
            step === 2 && builder.ruleMode === RuleMode.Validate
              ? builder.translate('pages:rules.builder.messageBlocker')
              : undefined
          }
          translate={builder.translate}
          onBack={() => {
            if (step === 0) closeModal();
            else goTo(step - 1);
          }}
          onNext={() => {
            if (!stepValid(step)) return;
            if (step === 3) void submit();
            else goTo(step + 1);
          }}
        />
      </Paper>
      <RuleCreatePreview builder={builder} />
    </Flex>
  );
};
